/**
 * Token persistence — both the access JWT (issued by `service-identity` via
 * `/v1/auth/login`) and the matching refresh token. The real login form on
 * `StaffLoginPage` populates these via `setSession`; the dev-mode "paste a
 * JWT" escape hatch on the same page still uses `setToken` directly.
 *
 * Pure module — does NOT touch React. Safe to import anywhere.
 */
const STORAGE_KEY = 'upmp_token';
const REFRESH_STORAGE_KEY = 'upmp.staff.refresh';

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    // Some embedded contexts (sandboxed iframes, server) throw on access.
    return null;
  }
}

export function getToken(): string | null {
  const storage = safeLocalStorage();
  if (!storage) {
    return null;
  }
  const value = storage.getItem(STORAGE_KEY);
  return value && value.length > 0 ? value : null;
}

export function setToken(token: string): void {
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }
  storage.setItem(STORAGE_KEY, token);
}

export function clearToken(): void {
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }
  storage.removeItem(STORAGE_KEY);
}

/**
 * Refresh-token accessor. The identity service returns a refresh token in the
 * `/v1/auth/login` response body in addition to setting the cookie; we
 * persist it so the api-client's 401 interceptor can POST `/v1/auth/refresh`
 * even when the cookie isn't being forwarded (cross-origin dev).
 */
export function getRefreshToken(): string | null {
  const storage = safeLocalStorage();
  if (!storage) return null;
  const value = storage.getItem(REFRESH_STORAGE_KEY);
  return value && value.length > 0 ? value : null;
}

export function setRefreshToken(token: string): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.setItem(REFRESH_STORAGE_KEY, token);
}

export function clearRefreshToken(): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.removeItem(REFRESH_STORAGE_KEY);
}

export const TOKEN_STORAGE_KEY = STORAGE_KEY;
export const REFRESH_TOKEN_STORAGE_KEY = REFRESH_STORAGE_KEY;
